import { useMemo, useRef, useState, useEffect, useCallback, forwardRef } from 'react';
import { WorkflowBlock, Connector, BlockGroup, BlockDimensions } from '@/types/workflow';
import { cn } from '@/lib/utils';
import { blockMatchesSearch } from '@/lib/minimapSearch';
import { BLOCK_WIDTH_PX, BLOCK_HEIGHT_PX } from '@/constants/canvas';
import { ChevronDown, Navigation, Box, FolderOpen, Folder, CornerDownRight } from 'lucide-react';
import type { Atlas } from '@/types/atlas';
import { DocOverlay } from './minimap/DocOverlay';
import { RefsLayer } from './minimap/RefsLayer';
import { SheetTabStrip } from './minimap/SheetTabStrip';
import { MinimapHeader } from './minimap/MinimapHeader';
import { HeatmapLayer } from './minimap/HeatmapLayer';
import { SearchOverlay } from './minimap/SearchOverlay';
import { CursorsLayer } from './minimap/CursorsLayer';
import { CrossSheetGhostsLayer } from './minimap/CrossSheetGhostsLayer';
import type { PeerCursor } from '@/lib/minimapCursors';
import { bucketCullForMinimap } from '@/lib/minimapVirtualization';
import { computeMinimapScale, makeWorldToCanvas, computeViewportRect, minimapClickToCanvas } from './minimap/draw';
import { IS_ATLAS_BUILD } from '@/lib/buildMode';
import type { MinimapSheetTab, MinimapBreadcrumbSegment, MinimapMode } from '@/types/minimap';

interface MinimapProps {
  blocks: WorkflowBlock[];
  connectors: Connector[];
  groups: BlockGroup[];
  canvasWidth: number;
  canvasHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  scrollLeft: number;
  scrollTop: number;
  zoom: number;
  blockDimensions?: Record<string, BlockDimensions>;
  onNavigate: (x: number, y: number) => void;
  onNavigateToGroup?: (groupId: string | null) => void;
  embedded?: boolean;
  /**
   * Case-insensitive substring to highlight in the minimap. Matching
   * blocks get a primary-color ring + full opacity; non-matching
   * blocks fade. Empty string = no filter (default).
   */
  searchQuery?: string;
  /**
   * Atlas manifest providing context for atlas-aware overlays (Phase B+).
   * When provided, BlockGroups with `atlasRef` are rendered with a
   * doc-icon badge and become drill-down targets.
   *
   * **CALLER MUST TIER-GATE.** This component is intentionally pure;
   * callers must check `useMinimapCapabilities().canUseAtlasMinimap`
   * (Pro+) before passing atlas data. As a defensive backstop, the
   * component silently ignores `atlas` when `IS_ATLAS_DISABLED` build
   * flag is set. See documents/v1/SYSTEM_STATUS.md for the isolation
   * policy.
   */
  atlas?: Atlas | null;
  /**
   * Document id of the workflow currently being viewed inside an atlas.
   * Triggers the "you are here" pulse on that doc rectangle (Phase B).
   */
  currentDocId?: string;
  /**
   * Callback when user double-clicks an atlas-doc rectangle. Enables
   * drill-down to /atlas/doc/<id>. Phase B.
   */
  onDrillDownDoc?: (documentId: string, sheetId?: string) => void;
  /**
   * When true, draws cross-document reference polylines between
   * visible atlas-doc rectangles. Phase C. Defaults to false.
   * Tier-gate via useMinimapCapabilities().canShowCrossDocRefs.
   */
  showAtlasRefs?: boolean;
  /**
   * Click handler for a ref polyline. Useful for navigating to the
   * source/target doc or showing ref details. Phase C.
   */
  onRefClick?: (refId: string) => void;
  /**
   * Sheet pills shown above the minimap canvas. Hidden automatically
   * when length ≤ 1. Phase D.
   */
  sheets?: MinimapSheetTab[];
  /**
   * Callback when the user clicks a sheet pill. Phase D.
   */
  onSwitchSheet?: (sheetId: string) => void;
  /**
   * Breadcrumb segments above the canvas — Atlas › Category › Doc › Sheet.
   * Hidden when empty. Phase E.
   */
  breadcrumb?: MinimapBreadcrumbSegment[];
  /**
   * When true, the breadcrumb shows a "← atlas" pill on the right.
   * Phase E.
   */
  showBackToAtlas?: boolean;
  /**
   * Render mode: 'normal' (blocks + groups), 'heatmap-density' (10×10
   * block-density heatmap), or 'heatmap-flow' (connector-flow heatmap).
   * Phase F. Defaults to 'normal'. Tier-gate via
   * useMinimapCapabilities().canShowAdvancedOverlays.
   */
  mode?: MinimapMode;
  /** When true, large workflows (>300 blocks) render via the
   *  virtualizeMinimap helper. Phase F. Defaults to true. */
  virtualize?: boolean;
  /**
   * Set of block ids matching the active Search Canvas query — pulls
   * from useLiveFilter() upstream. When non-empty, matched blocks get
   * a primary-color outline. When empty + activeQuery non-empty, the
   * "→ search canvas" pill renders. Phase G.
   */
  matchedBlockIds?: ReadonlySet<string>;
  /**
   * Ordered probe-trail block ids — earlier hits the user navigated
   * through. Rendered as dots that fade with index. Phase G.
   */
  probeTrailBlockIds?: readonly string[];
  /**
   * Active search query, used by the no-matches pill. Phase G.
   */
  activeSearchQuery?: string;
  /**
   * Live collaborator cursors. Stale entries fade per the
   * activeCursors() TTL helper. Phase G-bis. Tier-gate via
   * useMinimapCapabilities().canShowAdvancedOverlays.
   */
  peerCursors?: PeerCursor[];
  /**
   * Cross-sheet connectors for the whole workflow — used by
   * CrossSheetGhostsLayer to draw faint dashed lines from active-
   * sheet blocks to the SheetTabStrip pill of the target sheet.
   * Phase D.5. Optional; ghosts hidden when empty or when sheetCount
   * is < 2.
   */
  crossSheetConnectors?: import('@/types/workflow').CrossSheetConnector[];
  /** Index of the active sheet (matches `sheets[].isActive`). Phase D.5. */
  activeSheetIndex?: number;
}


const MINIMAP_WIDTH = 160;
const MINIMAP_HEIGHT = 100;

interface NavigatorItem {
  id: string;
  label: string;
  type: 'group' | 'block';
  color: string;
  x: number;
  y: number;
  depth: number;
  collapsed?: boolean;
  parentGroupId?: string;
  groupPath: string[];
  childGroupCount: number;
  blockCount: number;
}

function buildGroupPath(groupId: string | undefined, groups: BlockGroup[]): string[] {
  const path: string[] = [];
  let current = groupId;
  while (current) {
    path.unshift(current);
    const g = groups.find(gr => gr.id === current);
    current = g?.parentGroupId;
  }
  return path;
}

interface EntityNavigatorProps {
  blocks: WorkflowBlock[];
  groups: BlockGroup[];
  canvasWidth: number;
  canvasHeight: number;
  zoom: number;
  onNavigate: (x: number, y: number) => void;
  onNavigateToGroup?: (groupId: string | null) => void;
}

const EntityNavigator = forwardRef<HTMLDivElement, EntityNavigatorProps>(function EntityNavigator({
  blocks, groups, canvasWidth, canvasHeight, zoom, onNavigate, onNavigateToGroup,
}, _forwardedRef) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => searchRef.current?.focus());
    } else {
      setSearch('');
    }
  }, [isOpen]);

  const items = useMemo(() => {
    const result: NavigatorItem[] = [];

    function addGroup(group: BlockGroup, depth: number) {
      const groupBlocks = blocks.filter(b => group.blockIds.includes(b.id));
      const childGroups = groups.filter(g => g.parentGroupId === group.id);
      const center = groupBlocks.length > 0
        ? {
            x: groupBlocks.reduce((s, b) => s + b.x, 0) / groupBlocks.length,
            y: groupBlocks.reduce((s, b) => s + b.y, 0) / groupBlocks.length,
          }
        : { x: group.collapsedX ?? 0, y: group.collapsedY ?? 0 };

      const groupPath = buildGroupPath(group.id, groups);

      result.push({
        id: group.id,
        label: group.name || 'Group',
        type: 'group',
        color: group.color,
        x: center.x,
        y: center.y,
        depth,
        collapsed: group.collapsed,
        parentGroupId: group.parentGroupId,
        groupPath,
        childGroupCount: childGroups.length,
        blockCount: groupBlocks.length,
      });

      for (const block of groupBlocks) {
        result.push({
          id: block.id,
          label: block.config.name,
          type: 'block',
          color: block.color,
          x: block.x,
          y: block.y,
          depth: depth + 1,
          parentGroupId: group.id,
          groupPath,
          childGroupCount: 0,
          blockCount: 0,
        });
      }

      for (const child of childGroups) {
        addGroup(child, depth + 1);
      }
    }

    const topGroups = groups.filter(g => !g.parentGroupId);
    for (const g of topGroups) {
      addGroup(g, 0);
    }

    const allGroupedBlockIds = new Set(groups.flatMap(g => g.blockIds));
    const ungrouped = blocks.filter(b => !allGroupedBlockIds.has(b.id));
    for (const block of ungrouped) {
      result.push({
        id: block.id,
        label: block.config.name,
        type: 'block',
        color: block.color,
        x: block.x,
        y: block.y,
        depth: 0,
        groupPath: [],
        childGroupCount: 0,
        blockCount: 0,
      });
    }

    return result;
  }, [blocks, groups]);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter(item => item.label.toLowerCase().includes(q));
  }, [items, search]);

  const handleGoTo = useCallback((item: NavigatorItem) => {
    if (onNavigateToGroup) {
      if (item.type === 'group') {
        if (item.collapsed) {
          const parentId = item.parentGroupId ?? null;
          onNavigateToGroup(parentId);
        } else {
          onNavigateToGroup(item.id);
        }
      } else {
        const parentId = item.parentGroupId ?? null;
        onNavigateToGroup(parentId);
      }
    }

    setTimeout(() => {
      const screenX = item.x * zoom + canvasWidth / 2;
      const screenY = item.y * zoom + canvasHeight / 2;
      onNavigate(screenX, screenY);
    }, onNavigateToGroup ? 50 : 0);

    setSelectedId(item.id);
    setIsOpen(false);
  }, [zoom, canvasWidth, canvasHeight, onNavigate, onNavigateToGroup]);

  const selectedItem = selectedId ? items.find(i => i.id === selectedId) : null;

  const selectedPath = useMemo(() => {
    if (!selectedItem) return null;
    if (selectedItem.groupPath.length === 0) return null;
    return selectedItem.groupPath.map(gId => {
      const g = groups.find(gr => gr.id === gId);
      return g?.name || 'Group';
    });
  }, [selectedItem, groups]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md border bg-background hover:bg-accent/50 transition-colors text-left"
      >
        {selectedItem ? (
          <>
            {selectedItem.type === 'group' ? (
              selectedItem.collapsed
                ? <Folder className="w-3.5 h-3.5 flex-shrink-0" style={{ color: selectedItem.color }} />
                : <FolderOpen className="w-3.5 h-3.5 flex-shrink-0" style={{ color: selectedItem.color }} />
            ) : (
              <Box className="w-3.5 h-3.5 flex-shrink-0" style={{ color: selectedItem.color }} />
            )}
            <div className="flex flex-col flex-1 min-w-0">
              <span className="text-xs font-medium truncate">{selectedItem.label}</span>
              {selectedPath && (
                <span className="text-[10px] text-muted-foreground truncate">
                  {selectedPath.join(' › ')}
                </span>
              )}
            </div>
          </>
        ) : (
          <>
            <Navigation className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
            <span className="text-xs text-muted-foreground flex-1">Gå till...</span>
          </>
        )}
        <ChevronDown className={cn("w-3.5 h-3.5 text-muted-foreground flex-shrink-0 transition-transform", isOpen && "rotate-180")} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-popover border rounded-lg shadow-lg overflow-hidden">
          <div className="p-2 border-b">
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Sök grupp eller block..."
              className="w-full text-xs px-2 py-1.5 rounded-md border bg-background outline-none focus:ring-1 focus:ring-ring"
            />
          </div>

          <div className="max-h-[280px] overflow-y-auto p-1">
            {filtered.length === 0 && (
              <div className="text-xs text-muted-foreground text-center py-4">
                Inga resultat
              </div>
            )}
            {filtered.map(item => {
              const showPath = search.trim() && item.groupPath.length > 0;
              const pathLabel = showPath
                ? item.groupPath.map(gId => {
                    const g = groups.find(gr => gr.id === gId);
                    return g?.name || 'Group';
                  }).join(' › ')
                : null;

              return (
                <button
                  key={item.id}
                  onClick={() => handleGoTo(item)}
                  className={cn(
                    "w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left hover:bg-accent/50 transition-colors group",
                    selectedId === item.id && "bg-accent"
                  )}
                  style={{ paddingLeft: search.trim() ? 8 : item.depth * 14 + 8 }}
                >
                  {item.type === 'group' ? (
                    item.collapsed ? (
                      <Folder className="w-3.5 h-3.5 flex-shrink-0" style={{ color: item.color }} />
                    ) : (
                      <FolderOpen className="w-3.5 h-3.5 flex-shrink-0" style={{ color: item.color }} />
                    )
                  ) : (
                    <Box className="w-3.5 h-3.5 flex-shrink-0" style={{ color: item.color }} />
                  )}
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className="text-xs truncate" style={{ color: item.type === 'group' ? item.color : undefined }}>
                      {item.label}
                    </span>
                    {pathLabel && (
                      <span className="text-[10px] text-muted-foreground truncate">
                        {pathLabel}
                      </span>
                    )}
                  </div>
                  {item.type === 'group' && (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {item.childGroupCount > 0 && (
                        <span className="text-[10px] text-muted-foreground bg-muted px-1 rounded">
                          {item.childGroupCount} sub
                        </span>
                      )}
                      <span className="text-[10px] text-muted-foreground bg-muted px-1 rounded">
                        {item.blockCount}
                      </span>
                      <CornerDownRight className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-60 transition-opacity" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
});

export function Minimap({
  blocks, connectors, groups,
  canvasWidth, canvasHeight, viewportWidth, viewportHeight,
  scrollLeft, scrollTop, zoom, blockDimensions,
  onNavigate, onNavigateToGroup, embedded = false,
  searchQuery = '',
  atlas: atlasProp,
  currentDocId,
  onDrillDownDoc,
  showAtlasRefs = false,
  onRefClick,
  sheets,
  onSwitchSheet,
  breadcrumb,
  showBackToAtlas = false,
  mode = 'normal',
  virtualize = true,
  matchedBlockIds,
  probeTrailBlockIds,
  activeSearchQuery,
  peerCursors,
  crossSheetConnectors,
  activeSheetIndex,
}: MinimapProps) {
  // Defensive backstop for the Atlas isolation policy: when the build
  // flag VITE_DISABLE_ATLAS=true is set, drop the atlas prop on the
  // floor regardless of caller. Tier checks remain the caller's job
  // for the runtime path. See documents/v1/SYSTEM_STATUS.md.
  const atlas = IS_ATLAS_BUILD ? atlasProp : null;
  const activeSearch = searchQuery.trim().length > 0;
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ width: MINIMAP_WIDTH, height: MINIMAP_HEIGHT });

  useEffect(() => {
    if (!embedded || !containerRef.current) return;

    const updateSize = () => {
      if (containerRef.current) {
        setContainerSize({
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight,
        });
      }
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [embedded]);

  const minimapWidth = embedded ? containerSize.width : MINIMAP_WIDTH;
  const minimapHeight = embedded ? containerSize.height : MINIMAP_HEIGHT;

  const scale = computeMinimapScale(minimapWidth, minimapHeight, canvasWidth, canvasHeight);

  // Phase F: pixel-bucket culling. Once the workflow exceeds ~300 blocks
  // each block dot at minimap scale typically lands in the same minimap-
  // pixel as several others, so paint cycles are wasted. We bucket by
  // (Math.floor(x*scale), Math.floor(y*scale)) and keep one block per
  // bucket — search hits, probe-trail, and peer-cursor anchors are
  // force-kept so user focus points are never dropped.
  //
  // Groups + SearchOverlay still receive the full block array because
  // they compute bounding boxes / matched-rects that must reflect the
  // full document, not just what survives the cull.
  const cullResult = useMemo(() => {
    if (!virtualize || blocks.length <= 300) return null;
    const minimapBlocks = blocks.map((b) => ({
      id: b.id, x: b.x, y: b.y,
      width: blockDimensions?.[b.id]?.width ?? BLOCK_WIDTH_PX,
      height: blockDimensions?.[b.id]?.height ?? BLOCK_HEIGHT_PX,
    }));
    const minimapConns = connectors.map((c) => ({ id: c.id, fromId: c.fromId, toId: c.toId }));
    const forceKeep = new Set<string>();
    if (matchedBlockIds) for (const id of matchedBlockIds) forceKeep.add(id);
    if (probeTrailBlockIds) for (const id of probeTrailBlockIds) forceKeep.add(id);
    return bucketCullForMinimap(minimapBlocks, minimapConns, scale, {
      minThreshold: 300, forceKeep,
    });
  }, [blocks, connectors, blockDimensions, virtualize, scale, matchedBlockIds, probeTrailBlockIds]);

  const virtStats = cullResult
    ? { renderedBlocks: cullResult.stats.renderedBlocks, totalBlocks: cullResult.stats.totalBlocks }
    : null;

  const { toScreenX, toScreenY, toScreenSize } = makeWorldToCanvas(zoom, canvasWidth, canvasHeight);

  const viewportRect = useMemo(() => computeViewportRect({
    scrollLeft, scrollTop, viewportWidth, viewportHeight,
    canvasWidth, canvasHeight, scale, minimapWidth, minimapHeight,
  }), [scrollLeft, scrollTop, viewportWidth, viewportHeight, canvasWidth, canvasHeight, scale, minimapWidth, minimapHeight]);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const { x: canvasX, y: canvasY } = minimapClickToCanvas(
      e.clientX - rect.left,
      e.clientY - rect.top,
      scale,
    );
    onNavigate(canvasX, canvasY);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const step = 120;
    const centerX = scrollLeft + viewportWidth / 2;
    const centerY = scrollTop + viewportHeight / 2;
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        onNavigate(centerX - step, centerY);
        break;
      case 'ArrowRight':
        e.preventDefault();
        onNavigate(centerX + step, centerY);
        break;
      case 'ArrowUp':
        e.preventDefault();
        onNavigate(centerX, centerY - step);
        break;
      case 'ArrowDown':
        e.preventDefault();
        onNavigate(centerX, centerY + step);
        break;
      case ' ':
      case 'Space':
        e.preventDefault();
        onNavigate(canvasWidth / 2, canvasHeight / 2);
        break;
    }
  };

  return (
    <div className="flex flex-col gap-2" data-guide="minimap">
      {embedded && (
        <EntityNavigator
          blocks={blocks}
          groups={groups}
          canvasWidth={canvasWidth}
          canvasHeight={canvasHeight}
          zoom={zoom}
          onNavigate={onNavigate}
          onNavigateToGroup={onNavigateToGroup}
        />
      )}

      {/* Phase E: breadcrumb above the canvas. */}
      {breadcrumb && breadcrumb.length > 0 && (
        <MinimapHeader segments={breadcrumb} showBackToAtlas={showBackToAtlas} />
      )}

      {/* Phase D: sheet tab strip — only renders when 2+ sheets. */}
      {sheets && sheets.length > 1 && (
        <SheetTabStrip tabs={sheets} onSwitchSheet={onSwitchSheet} />
      )}

      <div
        ref={containerRef}
        className={cn(
          "bg-card/95 backdrop-blur-sm rounded-lg border border-border shadow-lg overflow-hidden cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          embedded ? "w-full aspect-[16/10]" : "absolute bottom-4 right-4 z-20"
        )}
        data-minimap-atlas-mode={atlas ? 'on' : 'off'}
        style={embedded ? undefined : { width: MINIMAP_WIDTH, height: MINIMAP_HEIGHT }}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="application"
        aria-label="Minimap (arrow keys pan, Space recenters)"
      >
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              'linear-gradient(to right, hsl(var(--canvas-grid)) 1px, transparent 1px), linear-gradient(to bottom, hsl(var(--canvas-grid)) 1px, transparent 1px)',
            backgroundSize: `${20 * scale}px ${20 * scale}px`,
          }}
        />

        {groups.filter(g => g.collapsed && !g.atlasRef && g.collapsedX != null && g.collapsedY != null).map(group => {
          const cardW = 200;
          const cardH = 70;
          return (
            <div
              key={`collapsed-${group.id}`}
              className="absolute rounded-sm"
              style={{
                left: toScreenX(group.collapsedX! - cardW / 2) * scale,
                top: toScreenY(group.collapsedY! - cardH / 2) * scale,
                width: toScreenSize(cardW) * scale,
                height: toScreenSize(cardH) * scale,
                backgroundColor: group.color,
                opacity: 0.8,
              }}
            />
          );
        })}

        {/* Atlas docs (Phase B) — render with their own styling. Below
            the regular-group block so they sit just under connectors. */}
        <DocOverlay
          groups={groups}
          blocks={blocks}
          blockDimensions={blockDimensions}
          toScreenX={toScreenX}
          toScreenY={toScreenY}
          toScreenSize={toScreenSize}
          scale={scale}
          atlas={atlas}
          currentDocId={currentDocId}
          onDrillDownDoc={onDrillDownDoc}
        />

        {/* Cross-document refs (Phase C) — toggle-controlled polylines. */}
        {atlas && showAtlasRefs && (
          <RefsLayer
            groups={groups}
            blocks={blocks}
            blockDimensions={blockDimensions}
            toScreenX={toScreenX}
            toScreenY={toScreenY}
            toScreenSize={toScreenSize}
            scale={scale}
            atlas={atlas}
            width={minimapWidth}
            height={minimapHeight}
            onRefClick={onRefClick}
          />
        )}

        {/* Heatmap (Phase F) — block-density or connector-flow grid. */}
        {mode !== 'normal' && (
          <HeatmapLayer
            mode={mode}
            blocks={blocks}
            connectors={connectors}
            canvasWidth={canvasWidth}
            canvasHeight={canvasHeight}
            width={minimapWidth}
            height={minimapHeight}
            toScreenX={toScreenX}
            toScreenY={toScreenY}
            scale={scale}
          />
        )}

        {/* Virtualization badge — only when active. */}
        {virtStats && (
          <div className="absolute bottom-1 left-1 rounded bg-background/85 px-1 py-0.5 text-[8px] text-muted-foreground pointer-events-none">
            {virtStats.renderedBlocks}/{virtStats.totalBlocks}
          </div>
        )}

        {/* Search Canvas integration (Phase G) — matched blocks
            outlined; probe trail rendered as fading dots; "no
            matches → /search" pill when query is active but the
            active sheet has zero hits. */}
        {(matchedBlockIds || activeSearchQuery) && (
          <SearchOverlay
            matchedBlockIds={matchedBlockIds ?? new Set()}
            probeTrailBlockIds={probeTrailBlockIds}
            noMatchesQuery={activeSearchQuery}
            blocks={blocks}
            blockDimensions={blockDimensions}
            toScreenX={toScreenX}
            toScreenY={toScreenY}
            toScreenSize={toScreenSize}
            scale={scale}
            width={minimapWidth}
            height={minimapHeight}
          />
        )}

        {/* Phase G-bis: collaborator cursor dots — gated upstream by
            useMinimapCapabilities().canShowAdvancedOverlays. */}
        {peerCursors && peerCursors.length > 0 && (
          <CursorsLayer
            peers={peerCursors}
            canvasWidth={canvasWidth}
            canvasHeight={canvasHeight}
            minimapWidth={minimapWidth}
            minimapHeight={minimapHeight}
          />
        )}

        {/* Phase D.5: cross-sheet connector ghosts — dashed lines from
            an active-sheet block to the SheetTabStrip pill of the
            target sheet. Hidden when the workflow is single-sheet or
            no cross-sheet connectors exist. */}
        {crossSheetConnectors && crossSheetConnectors.length > 0 && (sheets?.length ?? 0) >= 2 && (
          <CrossSheetGhostsLayer
            blocks={blocks}
            blockDimensions={blockDimensions}
            crossSheetConnectors={crossSheetConnectors}
            activeSheetIndex={activeSheetIndex ?? 0}
            sheetCount={sheets?.length ?? 0}
            toScreenX={toScreenX}
            toScreenY={toScreenY}
            scale={scale}
            width={minimapWidth}
            height={minimapHeight}
          />
        )}

        {groups.filter(g => !g.collapsed && !g.atlasRef).map(group => {
          const groupBlocks = blocks.filter(b => group.blockIds.includes(b.id));
          if (groupBlocks.length === 0) return null;

          const minX = Math.min(...groupBlocks.map(b => {
            const w = blockDimensions?.[b.id]?.width || BLOCK_WIDTH_PX;
            return b.x - w / 2;
          }));
          const minY = Math.min(...groupBlocks.map(b => {
            const h = blockDimensions?.[b.id]?.height || BLOCK_HEIGHT_PX;
            return b.y - h / 2;
          }));
          const maxX = Math.max(...groupBlocks.map(b => {
            const w = blockDimensions?.[b.id]?.width || BLOCK_WIDTH_PX;
            return b.x + w / 2;
          }));
          const maxY = Math.max(...groupBlocks.map(b => {
            const h = blockDimensions?.[b.id]?.height || BLOCK_HEIGHT_PX;
            return b.y + h / 2;
          }));

          return (
            <div
              key={group.id}
              className="absolute rounded-sm opacity-30"
              style={{
                left: toScreenX(minX - 16) * scale,
                top: toScreenY(minY - 32) * scale,
                width: toScreenSize(maxX - minX + 32) * scale,
                height: toScreenSize(maxY - minY + 48) * scale,
                backgroundColor: group.color,
              }}
            />
          );
        })}

        <svg className="absolute inset-0 pointer-events-none" width={minimapWidth} height={minimapHeight}>
          {connectors.map(connector => {
            if (cullResult && !cullResult.visibleConnectorIds.has(connector.id)) return null;
            const fromBlock = blocks.find(b => b.id === connector.fromId);
            const toBlock = blocks.find(b => b.id === connector.toId);
            if (!fromBlock || !toBlock) return null;

            const fromX = toScreenX(fromBlock.x) * scale;
            const fromY = toScreenY(fromBlock.y) * scale;
            const toX = toScreenX(toBlock.x) * scale;
            const toY = toScreenY(toBlock.y) * scale;

            return (
              <line
                key={connector.id}
                x1={fromX}
                y1={fromY}
                x2={toX}
                y2={toY}
                stroke="hsl(var(--primary))"
                strokeWidth={1}
                opacity={0.6}
              />
            );
          })}
        </svg>

        {blocks.map(block => {
          if (cullResult && !cullResult.visibleBlockIds.has(block.id)) return null;
          const w = blockDimensions?.[block.id]?.width || BLOCK_WIDTH_PX;
          const h = blockDimensions?.[block.id]?.height || BLOCK_HEIGHT_PX;
          const matches = activeSearch ? blockMatchesSearch(block, searchQuery) : true;

          return (
            <div
              key={block.id}
              className={cn(
                'absolute rounded-sm',
                activeSearch && matches && 'ring-2 ring-primary',
              )}
              title={block.config.name || block.id}
              style={{
                left: toScreenX(block.x - w / 2) * scale,
                top: toScreenY(block.y - h / 2) * scale,
                width: toScreenSize(w) * scale,
                height: toScreenSize(h) * scale,
                backgroundColor: block.color,
                opacity: activeSearch ? (matches ? 1 : 0.15) : 0.8,
              }}
            />
          );
        })}

        <div
          className="absolute border-2 border-primary bg-primary/10 rounded-sm pointer-events-none"
          style={{
            left: viewportRect.x,
            top: viewportRect.y,
            width: viewportRect.width,
            height: viewportRect.height,
          }}
        />

        {!embedded && (
          <div className="absolute bottom-1 left-1 text-[8px] text-muted-foreground font-medium uppercase tracking-wider">
            Minimap
          </div>
        )}
      </div>
    </div>
  );
}
